import { LayerProps } from 'react-map-gl/mapbox';
import { WebMercatorViewport } from '@deck.gl/core/typed';
import { Units } from '@turf/helpers';
import { Geometry } from 'geojson';
import { EasingOptions } from 'mapbox-gl';

import { GeoLocation } from '../cerberus';

import {
  BoundsInfo,
  Heatmap,
  HeatmapColorTheme,
  Markers,
  ViewportState,
} from './HcMapLayers.types';
import { MapStyles } from './MapStyles.types';

export interface MoveMapProps {
  longitude: number;
  latitude: number;
  zoom?: number;
  height?: number;
  width?: number;
}

export interface FitGeoLocationsReturn {
  coords: { lng: number; lat: number };
  viewport: WebMercatorViewport;
}

export enum ControlPosition {
  TopLeft = 'top-left',
  TopRight = 'top-right',
  BottomRight = 'bottom-right',
  BottomLeft = 'bottom-left',
}

export interface ControlConfig {
  position: ControlPosition;
  offsetTop?: number;
  offsetLeft?: number;
}

export enum GeometryTypesEnum {
  CIRCLE = 'Circle',
  RECTANGLE = 'Rectangle',
  POLYGON = 'Polygon',
}

export interface FeatureProperties {
  shape: GeometryTypesEnum;
  drawType: DrawType;
  units?: Units;
  radius?: number;
}
export declare type DrawType = 'radius' | 'custom';

export interface TileCoords {
  x: number;
  y: number;
  zoom: number;
}

export interface LngLatObject {
  lng: number;
  lat: number;
}

export interface MapBounds {
  ne: LngLatObject;
  sw: LngLatObject;
}

/*
 * Creating ReactMapGlBounds type as it's a more specific and correct type (as a typescript tuple) as
 * the built in type is number[][] which is an array of number arrays of any length which is less specific
 * ReactMapGlBounds represents [[lon, lat], [lon, lat]] as the south west and north east corners of the bounds
 */
export type ReactMapGlLngLatPair = [number, number];
export type ReactMapGlBounds = [ReactMapGlLngLatPair, ReactMapGlLngLatPair];

export interface Viewport {
  latitude: number;
  longitude: number;
  zoom: number;
  width?: number;
  height?: number;
}

export type ViewportInitialized = Required<Viewport>;

export interface MarkerOffset {
  offsetTop: number;
  offsetLeft: number;
}

export interface MarkerAnimationStep {
  offset: MarkerOffset;
}

export interface MapOutlineConfig {
  id: string;
  geom: Geometry | undefined;
  layerStyle?: LayerProps[];
}

export interface FitBoundsPayload {
  mapId: string;
  coords: Omit<GeoLocation, 'precision'>[];
  padding?: number;
  /**
   * A string that represents when the coordinates
   * The map will re-fit bounds when the fitId changes
   */
  fitId: string;
}

export interface MlsCoveragePayload {
  mapId: string;
  visible: boolean;
  showLegend: 'lg' | 'sm' | false;
  warningVisible?: boolean;
  highlightCovered?: boolean;
  minZoom?: number;
}

export interface HcMapState {
  /** Id of the current fitBounds prop */
  fitId?: string;
  viewport: ViewportState;
  mapStyle: MapStyles;
  heatmap: Heatmap;
  markers: Markers;
  boundsInfo?: BoundsInfo;
  flyTo?: FlyTo;
  mlsCoverage?: Omit<MlsCoveragePayload, 'mapId'>;
  isMapMoving: boolean;
  debug?: boolean;
  heatmapTheme: HeatmapColorTheme;
}

export interface HcMapSliceState {
  [key: string]: HcMapState | undefined;
}

export interface HcMapPartialAppState {
  map: HcMapSliceState;
}

export type FlyTo = Omit<EasingOptions, 'center' | 'essential'> & {
  center: LngLatObject; // require coordinates for flyTo
};
